import { ENDPOINTS } from '../endpoints';
import { api } from '../client/axiosClient';
import { 
  UserRoleDto,
  RolePrivilegeDto
} from '../types/dtos';
import { CustomResponseEntity } from '../types/common';

export class AuthorizationService {
  /**
   * Get active roles for the authenticated user
   */
  async getUserRoles(organizationId: string, userId: string): Promise<CustomResponseEntity<UserRoleDto[]>> {
    return api.get(ENDPOINTS.userRoles.own(organizationId, userId));
  }

  /**
   * Get active privileges for a specific role of the authenticated user
   */
  async getRolePrivileges(roleId: string): Promise<CustomResponseEntity<RolePrivilegeDto[]>> {
    return api.get(ENDPOINTS.rolePrivileges.own(roleId));
  }

  /**
   * Get active privileges with full details for a specific role of the authenticated user
   */
  async getRolePrivilegesDetailed(roleId: string): Promise<CustomResponseEntity<RolePrivilegeDto[]>> {
    return api.get(ENDPOINTS.rolePrivileges.ownDetailed(roleId));
  }

  /**
   * Get all privileges for all roles of the authenticated user
   * This is a convenience method that fetches roles first, then privileges for each role
   */
  async getAllUserPrivileges(organizationId: string, userId: string): Promise<{
    userRoles: UserRoleDto[];
    rolePrivileges: Record<string, RolePrivilegeDto[]>;
  }> {
    const rolesResponse = await this.getUserRoles(organizationId, userId);
    const responseData = rolesResponse.data as any;

    if (responseData && typeof responseData === 'object' && 'userRoles' in responseData) {
      return {
        userRoles: responseData.userRoles || [],
        rolePrivileges: responseData.rolePrivileges || {},
      };
    }

    const userRoles = Array.isArray(responseData) ? responseData : [];
    const rolePrivileges: Record<string, RolePrivilegeDto[]> = {};

    await Promise.all(
      userRoles.map(async (userRole: UserRoleDto) => {
        try {
          const privilegesResponse = await this.getRolePrivilegesDetailed(userRole.roleId);
          rolePrivileges[userRole.roleId] = privilegesResponse.data || [];
        } catch (_error) {
          rolePrivileges[userRole.roleId] = [];
        }
      })
    );

    return {
      userRoles,
      rolePrivileges,
    };
  }

  /**
   * Get privileges for roles that belong to a specific organization
   */
  async getOrganizationPrivileges(organizationId: string, userId: string): Promise<{
    userRoles: UserRoleDto[];
    rolePrivileges: Record<string, RolePrivilegeDto[]>;
  }> {
    const allPrivileges = await this.getAllUserPrivileges(organizationId, userId);
    const filteredUserRoles: UserRoleDto[] = [];
    const filteredRolePrivileges: Record<string, RolePrivilegeDto[]> = {};

    for (const userRole of allPrivileges.userRoles) {
      try {
        const roleResponse = await api.get(ENDPOINTS.roles.byId(userRole.roleId));
        const role = (roleResponse as any).data;

        if (role && role.organizationId === organizationId) {
          filteredUserRoles.push(userRole);
          filteredRolePrivileges[userRole.roleId] = allPrivileges.rolePrivileges[userRole.roleId] || [];
        }
      } catch (_error) {
        // Skip this role if we can't fetch its details
      }
    }

    return {
      userRoles: filteredUserRoles,
      rolePrivileges: filteredRolePrivileges,
    };
  }
}

// Service instance
export const authorizationService = new AuthorizationService();
